import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { CourseTrackingCard } from "Components/CourseTrackingCard/CourseTrackingCard";
import { CourseTrackingCardSkeleton } from "Components/CourseTrackingCardSkeleton/CourseTrackingCardSkeleton";
import { PaqueteCardSkeleton } from "Components/PaqueteCardSkeleton/PaqueteCardSkeleton";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { Button } from "Components/Button/Button";
import { useSessionStorage } from "Hooks/useSessionStorage";
import { useQuery } from "Hooks/useQuery";
import { useMutation } from "Hooks/useMutation";
import {
  getPaqueteDetalle,
  asignPaquete,
} from "Services/paquete_cursos_service";
import { ULTIMO_PAQUETE } from "Constantes/constants";
import { RUTAS } from "rutas";
import { useNotificationTimer } from "Hooks/useNotificationTimer";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import "./Paquete.scss";

export const Paquete = () => {
  const navigate = useNavigate();
  const { idPaquete } = useParams();
  const { value: paqueteLocalStorage, setValue: setPaqueteLocalStorage } =
    useSessionStorage(ULTIMO_PAQUETE);
  const { authUser: user } = useSelector((state) => state.auth);
  const { data: paqueteData, isLoading: loading } = useQuery(
    getPaqueteDetalle,
    {
      idUsuario: user?.idUsuario,
      idPaquete: parseInt(idPaquete),
    },
    Boolean(idPaquete)
  );
  const { mutate: mutateAsignPaquete } = useMutation(asignPaquete);
  const { triggerNotificationFetch } = useNotificationTimer();
  const { refetch: refetchCursosPaquetes } = useCursosPaquetes();

  const getCurrentCursoCompletion = (curso) => {
    if (Boolean(user?.idUsuario)) {
      return curso.progreso?.porcentajeTotal === 100;
    }

    const isSamePaquete =
      parseInt(paqueteLocalStorage?.idPaquete) === parseInt(idPaquete);

    if (isSamePaquete) {
      const savedCurso = paqueteLocalStorage.cursos?.find(
        (c) => c.idCurso === curso.idCurso
      );

      if (savedCurso) {
        if (curso.materialesIds && Array.isArray(curso.materialesIds)) {
          const currentIds = curso.materialesIds.map(
            (item) => item.idContenidoCurso
          );

          const savedMaterialIds =
            savedCurso.materiales?.map((m) => m.idContenidoCurso) || [];
          const savedSurveyId = savedCurso.encuesta?.idContenidoCurso;
          const savedIds = savedSurveyId
            ? [...savedMaterialIds, savedSurveyId]
            : savedMaterialIds;

          const hasNewContent = currentIds.some((id) => !savedIds.includes(id));

          if (savedCurso.progreso?.porcentajeTotal === 100 && hasNewContent) {
            return false;
          }
        }

        return savedCurso.progreso?.porcentajeTotal === 100;
      }
    }

    return false;
  };

  const handleNavigatePrincipal = () => {
    if (loading) return;
    navigate(RUTAS.PRINCIPAL);
  };

  const handleCourseClick = (idCurso) => {
    navigate(RUTAS.PAQUETE_CURSO(idPaquete, idCurso));
  };

  const areAllCoursesCompleted = () => {
    if (!paqueteData?.cursos) return false;
    return paqueteData.cursos.every((curso) =>
      getCurrentCursoCompletion(curso)
    );
  };

  const handleFinalizarPaquete = () => {
    navigate(RUTAS.DIPLOMA_PAQUETE(idPaquete));

    if (Boolean(user?.idUsuario)) {
      setPaqueteLocalStorage(null);
    }
  };

  useEffect(() => {
    if (!loading && paqueteData === null) {
      navigate(RUTAS.PRINCIPAL);
    }
  }, [loading, paqueteData, navigate, idPaquete]);

  useEffect(() => {
    if (Boolean(user?.idUsuario) && paqueteData && !paqueteData.asignado) {
      mutateAsignPaquete({
        idUsuario: user.idUsuario,
        idPaqueteCursos: parseInt(idPaquete),
      });
      triggerNotificationFetch();
      refetchCursosPaquetes();
    }
  }, [user?.idUsuario, paqueteData, idPaquete]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      paqueteLocalStorage?.idPaquete === parseInt(idPaquete) &&
      paqueteData?.cursos
    ) {
      const activeCourseIds = paqueteData.cursos.map((c) => c.idCurso);
      const cursosActivos =
        paqueteLocalStorage.cursos?.filter((c) =>
          activeCourseIds.includes(c.idCurso)
        ) || [];

      if (cursosActivos.length !== (paqueteLocalStorage.cursos?.length || 0)) {
        setPaqueteLocalStorage({
          ...paqueteLocalStorage,
          cursos: cursosActivos,
        });
      }
    }
  }, [paqueteData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="paquete-container">
        <section className="paquete-container__breadcrumb">
          <Breadcrumbs aria-label="breadcrumb" className="text-gris-acero">
            <Link
              className="breadcrumb__default"
              onClick={handleNavigatePrincipal}
            >
              Inicio
            </Link>
            <Link className="breadcrumb__static">
              {loading ? "Cargando..." : paqueteData?.titulo}
            </Link>
          </Breadcrumbs>
        </section>
        <section className="paquete-container__sidebar">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <CourseTrackingCardSkeleton key={i} />
            ))}

          {!loading &&
            (!paqueteData?.cursos || paqueteData?.cursos?.length === 0) && (
              <InformativeText>
                <div>No hay cursos disponibles para este paquete.</div>
              </InformativeText>
            )}

          {!loading &&
            paqueteData?.cursos?.map((curso, index) => (
              <CourseTrackingCard
                key={index}
                variant="neutral"
                title={curso.titulo}
                completed={getCurrentCursoCompletion(curso)}
                duration={`${curso?.duracion || 0} minutos`}
                onClick={() => handleCourseClick(curso.idCurso)}
              />
            ))}
        </section>
        <section className="paquete-container__content">
          {loading && <PaqueteCardSkeleton />}
          {!loading && paqueteData && (
            <div className="paquete-card">
              <div className="paquete-card__header">
                <h2 className="paquete-card__title">{paqueteData.titulo}</h2>
                <div className="paquete-card__divider" />
                <span className="paquete-card__category">
                  {paqueteData.categoria}
                </span>
              </div>
              <p className="paquete-card__description">
                {paqueteData.descripcion || "Descripcion del paquete"}{" "}
              </p>
            </div>
          )}

          {!loading &&
            paqueteData?.cursos?.length > 0 &&
            !areAllCoursesCompleted() && (
              <InformativeText>
                <div>
                  ¡El aprendizaje comienza aquí!{" "}
                  <strong>Elige un curso del listado</strong> y da el primer
                  paso.
                </div>
              </InformativeText>
            )}

          {!loading && areAllCoursesCompleted() && (
            <>
              <InformativeText>
                <div>
                  ¡Has completado el aprendizaje!{" "}
                  <strong>Finaliza el paquete</strong> y obtén tu diploma.
                </div>
              </InformativeText>
              <div className="w-full">
                <Button
                  className="paquete-container__completion-button w-full"
                  onClick={handleFinalizarPaquete}
                >
                  FINALIZAR PAQUETE
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
      <Footer />
    </Layout>
  );
};
